import { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router";
import { useAuth } from "./useAuth";

export interface RequireAuthProps {
  children: React.ReactNode;
  /** Where to send unauthenticated users that prefer a login page over an immediate redirect. */
  loginPath?: string;
  /** Render while the auth state is resolving. */
  fallback?: React.ReactNode;
}

/**
 * RequireAuth — route guard. Triggers a login redirect (or routes to loginPath) when
 * the user is not authenticated.
 */
export function RequireAuth({ children, loginPath, fallback = null }: RequireAuthProps) {
  const { isAuthenticated, isLoading, login } = useAuth();
  const location = useLocation();

  // One redirect per signed-out spell. A failed signinRedirect (IdP discovery unreachable, DNS, CORS,
  // offline) cycles isLoading and rebuilds `login`, which re-ran this effect without end — a measured
  // ~900 discovery requests a second per open tab. A reload retries; signing in re-arms the guard so a
  // session lost later redirects again.
  const attempted = useRef(false);
  useEffect(() => {
    if (isAuthenticated) {
      attempted.current = false;
      return;
    }
    if (!isLoading && !loginPath && !attempted.current) {
      attempted.current = true;
      login();
    }
  }, [isLoading, isAuthenticated, loginPath, login]);

  if (isLoading) return <>{fallback}</>;
  if (!isAuthenticated) {
    return loginPath ? (
      <Navigate to={loginPath} replace state={{ from: location }} />
    ) : (
      <>{fallback}</>
    );
  }
  return <>{children}</>;
}
